import React, { useEffect, useState } from "react";
import {
  requestDeleteScheduledEbilling,
  requestListScheduledEbilling,
  resetDeleteScheduledEbilling,
  useAuthState,
  useEbillingDispatch,
  useEbillingState,
} from "../../Context";
import { useTranslation } from "react-i18next";
import TableSkeleton from "../../Components/TableSkeleton";
import { NumericFormat } from "react-number-format";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { getErrorMessages, isLastTokenNull } from "../../Utils";
import { Pagination } from "../../Components/Pagination";
import dayjs from "dayjs";
import { Button } from "../../Components/Button";
import { useNavigate } from "react-router-dom";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import { ModalAlert } from "../../Components/ModalAlert";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { periodOptions } from "../../Constants/ebillingPeriodOptions";
import Badge from "../../Components/Badge";

export const ebillingPeriods = [
  { label: "Previous", value: "PREVIOUS" },
  { label: "creation", value: "CREATION" },
];

const EbillingSchedule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuthState();

  const ebillingDispatch = useEbillingDispatch();
  const {
    ebillingSchedules,
    scheduleLoading,
    nextTokens,
    scheduleError,
    deleteScheduleID,
    deleteScheduleIDLoading,
    deleteScheduleIDError,
  } = useEbillingState();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [page, setpage] = useState(1);
  const [scheduleId, setscheduleId] = useState("");

  useEffect(() => {
    return () => {
      resetDeleteScheduledEbilling(ebillingDispatch);
      setscheduleId("");
    };
  }, [ebillingDispatch]);

  useEffect(() => {
    if (user?.id) {
      requestListScheduledEbilling(ebillingDispatch, {
        page: 1,
        userID: user.id,
      });
    }
  }, [user?.id, ebillingDispatch]);

  useEffect(() => {
    if (!deleteScheduleID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deleteScheduleID]);

  useEffect(() => {
    if (deleteScheduleIDError) {
      setShowConfirmModal(false);
      setShowErrorModal(true);
    }
  }, [deleteScheduleIDError]);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListScheduledEbilling(ebillingDispatch, {
        page: nextPage,
        userID: user.id,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListScheduledEbilling(ebillingDispatch, {
        page: prevPage,
        userID: user.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListScheduledEbilling(ebillingDispatch, {
        page: selectedPage,
        userID: user.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  const onDelete = () => {
    requestDeleteScheduledEbilling(ebillingDispatch, {
      id: scheduleId,
    });
  };

  const findPeriod = (item, type, previousValue) => {
    return ebillingPeriods.find((period) => {
      return item[type] === previousValue
        ? period.value === "PREVIOUS"
        : period.value === "CREATION";
    });
  };

  return (
    <div className="space-y-4">
      {deleteScheduleIDError && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(deleteScheduleIDError)}
        />
      )}
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="alertDelScheduleTitle"
        description="alertDelScheduledDescription"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deleteScheduleIDLoading}
        disabledButton={deleteScheduleIDLoading}
        onConfirm={onDelete}
      />
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description={"confirmDelScheduleDescription"}
        onConfirm={() => {
          resetDeleteScheduledEbilling(ebillingDispatch);
          setscheduleId("");
        }}
      />
      <div className="gap-4 flex justify-start sm:justify-end w-full">
        <Button
          type="button"
          onClick={() => navigate("/scheduled-ebilling/create")}
          title={t("createScheduleTitle")}
        />
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("ID")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("KAP - KJS")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Total")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("NPWP")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("periodMonth")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("years")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("dateCreation")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Date")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Expired")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>

                {scheduleLoading ? (
                  <TableSkeleton rows={3} columns={11} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {ebillingSchedules.length > 0 ? (
                      ebillingSchedules.map((item) => {
                        const shortId = item.id.slice(-5);
                        const ebillingData = {
                          period: findPeriod(
                            item,
                            "ebillingPeriod",
                            "PREVIOUS_MONTH"
                          ),
                          year: findPeriod(
                            item,
                            "ebillingYear",
                            "PREVIOUS_YEAR"
                          ),
                        };
                        const createPeriod = periodOptions.find(
                          (period) => period.value === item.createPeriod
                        );
                        const { period, year } = ebillingData;
                        const { kap, kjs, npwp } = item.ebilling.summary;
                        return (
                          <tr key={item.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap uppercase">
                              <a href={`/scheduled-ebilling/detail/${item.id}`}>
                                {shortId}
                              </a>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {kap} - {kjs}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                value={item.ebilling.total}
                                thousandSeparator={"."}
                                decimalSeparator={","}
                                prefix={"Rp"}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                value={npwp}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {t(period.label)}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {t(year.label)}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {t(createPeriod.label)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-normal">
                              {item.date}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {item.expiredAt
                                ? dayjs(item.expiredAt).format("DD-MMM-YYYY")
                                : null}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {item.status === "ACTIVE" ? (
                                <Badge title="Active" type="success" />
                              ) : null}
                              {item.status === "PAUSED" ? (
                                <Badge title="paused" type="secondary" />
                              ) : null}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <div className="flex flex-auto gap-2">
                                <button>
                                  <PencilAltIcon
                                    onClick={() =>
                                      navigate(`/scheduled-ebilling/${item.id}`)
                                    }
                                    className="h-5 w-5 text-blue-500"
                                  />
                                </button>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    setscheduleId(item.id);
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5 text-red-500" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <ErrorStateTable
                        colSpan={12}
                        error={scheduleError}
                        emptyTitle={
                          isLastTokenNull(nextTokens)
                            ? "No E-Billing"
                            : "Please click Next again to load the data"
                        }
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EbillingSchedule;
